import {
    Notification,
    INotificationProperties,
} from '@vfde-brix/ws10/notification';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import { startAppListening } from '../../../app/listener';
import { mountNotification } from '../../../components/Notification';
import { selectCustomerConfig } from '../../app/slice';
import {
    selectSelectedTradeInDevice,
    selectIsError,
    selectTradeInDevices,
    selectIsDeviceNotFound,
} from '../selectors';
import { selectIsTradeInSelected } from '../slice';
import { RootState } from '../../../app/store';

/**
 * Mounts a Notification
 */
export const mountNotificationForSuggestInputStatus = (containerId: string): Notification | null => {
    const notification = mountNotification(containerId);

    /* istanbul ignore if */
    if (!notification) {
        return null;
    }

    listenForUpdates(notification);

    return notification;
};

const listenForUpdates = (notification: Notification) => {
    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectCustomerConfig(currentState).isHardwareOnly !== selectCustomerConfig(previousState).isHardwareOnly
            || selectIsTradeInSelected(currentState) !== selectIsTradeInSelected(previousState)
            || selectSelectedTradeInDevice(currentState) !== selectSelectedTradeInDevice(previousState)
            || selectIsError(currentState) !== selectIsError(previousState)
            || selectTradeInDevices(currentState) !== selectTradeInDevices(previousState)
        ,
        effect: (_action, listenerApi) => {
            const currentState = listenerApi.getState();
            const previousState = listenerApi.getOriginalState();
            update(notification, currentState, previousState);
        },
    });
};

/**
 * Updates the tradeIn suggestInput notification based on the given state
 */
const update = (notification: Notification, currentState: RootState, previousState: RootState): void => {
    const currentIsError = selectIsError(currentState);
    const previousIsError = selectIsError(previousState);
    const currentIsDeviceNotFound = selectIsDeviceNotFound(currentState);
    const previousIsDeviceNotFound = selectIsDeviceNotFound(previousState);

    // the following conditions are important so that we only show the notifications after the error appeared
    // but not after the user toggled tradeIn or hardwareOnly (in that case the noticiation should disappear again)
    const isError = currentIsError && !previousIsError;
    const isDeviceNotFound = currentIsDeviceNotFound && !previousIsDeviceNotFound;

    const notificationProperties: Partial<INotificationProperties> = {};
    const { tradeIn: tradeInOptions } = window[ADDITIONAL_PAGE_OPTIONS].promos || {};

    if (isError) {
        // show API error notification
        notificationProperties.optState = 'error';
        notificationProperties.txtContent = tradeInOptions?.technicalErrorText;
    }
    else if (isDeviceNotFound) {
        // show 'device not found' notification
        notificationProperties.optState = 'info';
        notificationProperties.txtContent = tradeInOptions?.deviceNotFoundText;
    }

    notification.update(notificationProperties);

    const customerConfig = selectCustomerConfig(currentState);
    const { isHardwareOnly } = customerConfig;
    const isTradeInSelected = selectIsTradeInSelected(currentState);
    const selectedTradeInDevice = selectSelectedTradeInDevice(currentState);
    const shouldShowNotification =
        (isError || isDeviceNotFound)
        && !isHardwareOnly
        && isTradeInSelected
        && !selectedTradeInDevice
    ;

    notification.toggleContainer(!shouldShowNotification);
};
